import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { assignDeveloperUser } from "../api";
import { AssignDeveloperUserRequest, LeadTrackDTO } from "../dtos";
import { LEAD_TRACKS_QUERY_KEY } from "./use-lead-tracks.hook";
import { LEAD_TRACK_ACTIVITY_QUERY_KEY } from "./use-lead-track-activity.hook";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import { showToast } from "@/modules/common/utils";

interface UseAssignDeveloperUserOptions {
  trackUId: string;
  onSuccess?: (data: LeadTrackDTO) => void;
  onError?: (error: unknown) => void;
}

export const useAssignDeveloperUser = ({
  trackUId,
  onSuccess,
  onError,
}: UseAssignDeveloperUserOptions) => {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse<LeadTrackDTO>,
    { response?: { data?: { message?: string } } },
    AssignDeveloperUserRequest
  >({
    mutationFn: (data) => assignDeveloperUser(trackUId, data),
    onSuccess: (response) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: [LEAD_TRACKS_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [LEAD_TRACK_ACTIVITY_QUERY_KEY, trackUId],
      });

      showToast({
        type: ToastType.SUCCESS,
        message: "Agent reassigned successfully",
        toastId: TOAST_IDS.EDIT_LEAD_SUCCESS,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });

      onSuccess?.(response.data);
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to reassign agent";

      showToast({
        type: ToastType.ERROR,
        message: errorMessage,
        toastId: TOAST_IDS.EDIT_LEAD_ERROR,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });

      onError?.(error);
    },
  });
};

export default useAssignDeveloperUser;
